import React, { useState } from 'react';
import {
  Box,
  Chip,
  SxProps,
  TextField as MuiTextField,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FieldProps, ChipInputFieldSchema } from '../types/Form';
import { FieldWrapper } from './FieldWrapper';
import { SubLabelWithClearLink } from './SubLabelWithClearLink';

const chipsContainer: SxProps = {
  background: 'white',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1,
  padding: '16.5px 14px'
};

const inputStyle: SxProps = {
  width: '100%',
  flex: 1,
  alignItems: 'flex-start',
  border: '0 !important',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0 !important'
  },
  '& .MuiInputBase-input': {
    padding: '0 !important'
  },
  maxWidth: '100%'
};

const chipStyle: SxProps = {
  '& .MuiChip-deleteIcon': {
    color: 'primary.main',
    '&:hover': {
      color: 'primary.main'
    }
  }
};

export type ChipInputProps = FieldProps<ChipInputFieldSchema>;

export const ChipInput: React.FC<ChipInputProps> = ({
  name,
  value,
  field,
  errorMessage,
  onChange
}) => {
  const { palette } = useTheme();
  const {
    label,
    subLabel,
    containerSx,
    placeholder = '',
    isClearable = false,
    clearLabel,
    autoFocus,
    ...restField
  } = field;
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Ensure value is always an array
  const chipValues = Array.isArray(value) ? value : value ? [value] : [];

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Filter out current inputValue
    const filteredChips = chipValues.filter((chip) => chip !== inputValue);
    const newChips = [...filteredChips];
    if (event.target.value.trim().length !== 0) {
      newChips.push(event.target.value);
    }
    onChange(newChips);
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && inputValue.trim().length > 0) {
      event.preventDefault();
      setInputValue('');
    } else if (
      event.key === 'Backspace' &&
      inputValue.trim().length === 0 &&
      chipValues.length > 0
    ) {
      // Delete the last chip
      const lastChip = chipValues[chipValues.length - 1];
      handleDelete(lastChip);
    }
  };

  const handleDelete = (chipToDelete: string) => {
    const newValue = chipValues.filter((chip) => chip !== chipToDelete);
    if (inputValue === chipToDelete) {
      setInputValue('');
    }
    onChange(newValue);
  };

  return (
    <FieldWrapper
      label={label}
      subLabel={
        <SubLabelWithClearLink
          subLabel={subLabel}
          isClearable={isClearable}
          onClearClicked={() => onChange([])}
          clearLabel={clearLabel}
        />
      }
      errorMessage={errorMessage}
      sx={containerSx}
    >
      <Box
        sx={{
          ...chipsContainer,
          border: isFocused
            ? palette.border.large.primary.main
            : palette.border.small.grey.dark
        }}
      >
        {chipValues
          .filter(
            (chip, index) =>
              chip !== inputValue || index !== chipValues.length - 1
          )
          .map((chip, index) => (
            <Chip
              key={index}
              label={chip}
              onDelete={() => handleDelete(chip)}
              color="primary"
              sx={chipStyle}
              deleteIcon={<CloseIcon data-testid="delete-chip-button" />}
            />
          ))}
        <MuiTextField
          data-testid={`chip-input-${name}`}
          sx={inputStyle}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          error={!!errorMessage}
          placeholder={placeholder}
          variant="outlined"
          fullWidth
          autoFocus={autoFocus}
          inputProps={{
            'data-testid': 'chip-input-field'
          }}
          {...restField}
        />
      </Box>
    </FieldWrapper>
  );
};
